import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faRightToBracket, faBars, faSearch, faEnvelope, faBell } from "@fortawesome/free-solid-svg-icons";
import Flag from "react-flagkit";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const languages = [
  { code: "US", name: "English", flag: "US" },
  { code: "ES", name: "Spanish", flag: "ES" },
];

function Navbar({ toggleSidebar, username }) {
  const { t, i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState("US");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null); // <-- state for logo
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch organization logo on mount
  useEffect(() => {
    const fetchOrgSettings = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/organizations/getSettings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrgLogo(response.data.organization_logo); // base64 string
      } catch (err) {
        console.warn("Failed to fetch organization settings:", err);
      }
    };
    fetchOrgSettings();
  }, []);

  const handleLanguageClick = () => {
    setActiveDropdown(activeDropdown === "language" ? null : "language");
  };

  const handleProfileClick = () => {
    setActiveDropdown(activeDropdown === "profile" ? null : "profile");
  };

  const handleLanguageChange = (code) => {
    setSelectedLanguage(code);
    setActiveDropdown(null);
    i18n.changeLanguage(code);
  };

  const navigateToProfile = () => {
    navigate("/user-profile");
    setActiveDropdown(null);
  };

  return (
    <div className="bg-white shadow-md p-4 flex justify-between items-center">
      <div className="flex items-center">
        {/* Sidebar Toggle Icon */}
        <button
          onClick={toggleSidebar}
          className="p-3 mr-5 bg-white border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <FontAwesomeIcon icon={faBars} className="text-gray-500" />
        </button>

        <div>
          {/* Display the username or "Guest" if not logged in */}
          <h1 className="text-lg font-semibold">
            {t("hello_user")} {username}
          </h1>
          <p className="text-sm text-gray-600">{t("welcome")}</p>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative">
          <input
            type="text"
            placeholder={t("search_placeholder")}
            className="pl-4 pr-8 py-2 rounded-full bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="absolute right-2 top-2 text-gray-500">
            <FontAwesomeIcon icon={faSearch} />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button className="w-10 h-10 rounded-full bg-white border border-gray-300 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500">
            <FontAwesomeIcon icon={faBell} className="text-gray-500" />
          </button>

          <div className="relative">
            <button className="w-10 h-10 rounded-full bg-white border border-gray-300 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500">
              <FontAwesomeIcon icon={faEnvelope} className="text-gray-500" />
            </button>
            <span className="absolute top-0 right-0 inline-block w-4 h-4 bg-blue-600 rounded-full text-white text-xs leading-tight text-center">
              4
            </span>
          </div>

          <div className="flex items-center space-x-4">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={handleLanguageClick}
                className="flex items-center justify-center w-10 h-10 bg-white border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <Flag country={selectedLanguage} className="w-6 h-4" />
              </button>

              {activeDropdown === "language" && (
                <div className="absolute right-0 mt-2 bg-white border border-gray-300 rounded shadow-lg z-10 w-48">
                  <ul className="py-2">
                    {languages.map((lang) => (
                      <li
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className="px-4 py-2 flex items-center hover:bg-gray-100 cursor-pointer"
                      >
                        <Flag country={lang.flag} className="w-6 h-4 mr-2" />
                        <span>{lang.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Profile Dropdown */}
            <div className="relative">
            <button
              onClick={handleProfileClick}
              className="w-14 h-14 rounded-full overflow-hidden border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 p-0"
            >
              <img
                src={orgLogo || "/agro_farm_ims_logo.png"}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </button>

            {activeDropdown === "profile" && (
              <div className="absolute right-0 mt-2 bg-white border border-gray-300 rounded shadow-lg z-10 w-48">
                <ul className="text-gray-700">
                  <li
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={navigateToProfile}
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-2" /> View Profile
                  </li>
                  <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                    <FontAwesomeIcon icon={faRightToBracket} className="mr-2" /> Logout
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Navbar;
